import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
    Box,
    Divider,
    FormLabel,
    Input,
    Option,
    Select,
    Typography,
} from "@mui/joy";
import { ChevronDown } from "lucide-react";

import {
    DEFAULT_PHONE_COUNTRY,
    formatPhoneNumber,
    getPhoneCountry,
    PHONE_COUNTRIES,
    type PhoneCountryCode,
} from "../utils/phone";

interface PhoneFieldProps {
    name: string;
    label: string;
    /** Form field for selected country code (US, CA, MX, IN). Defaults to customerPhoneCountry. */
    countryName?: string;
    placeholder?: string;
    helperText?: string;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    sx?: object;
}

function FlagIcon({
    src,
    alt,
    size = 20,
}: {
    src: string;
    alt: string;
    size?: number;
}) {
    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                objectFit: "cover",
                flexShrink: 0,
                display: "block",
            }}
        />
    );
}

/** Joined country + phone control with hyphen formatting. */
const PhoneField: React.FC<PhoneFieldProps> = ({
    name,
    label,
    countryName = "customerPhoneCountry",
    placeholder,
    helperText,
    size = "md",
    disabled,
    sx,
}) => {
    const { control, setValue, getValues } = useFormContext();
    const countryCode =
        (useWatch({ control, name: countryName }) as PhoneCountryCode) ||
        DEFAULT_PHONE_COUNTRY;
    const country = getPhoneCountry(countryCode);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const hasError = !!fieldState.error;

                // Avoid Joy FormControl: Select inside Input as decorator still
                // trips "only one control" if FormControl wraps both.
                return (
                    <Box sx={{ width: "100%" }}>
                        <FormLabel
                            sx={{
                                mb: 0,
                                color: hasError ? "danger.plainColor" : undefined,
                            }}
                        >
                            {label}
                        </FormLabel>
                        {helperText && !hasError && (
                            <Typography
                                level="body-xs"
                                sx={{ color: "text.secondary", mb: 1, mt: -0.7 }}
                            >
                                {helperText}
                            </Typography>
                        )}
                        {hasError && (
                            <Typography
                                level="body-xs"
                                color="danger"
                                sx={{ mb: 1, mt: -0.7 }}
                            >
                                {fieldState.error?.message}
                            </Typography>
                        )}

                        <Input
                            value={field.value ?? ""}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            placeholder={placeholder || country.placeholder}
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel-national"
                            disabled={disabled}
                            size={size}
                            error={hasError}
                            onChange={(event) => {
                                field.onChange(
                                    formatPhoneNumber(
                                        event.target.value,
                                        countryCode,
                                    ),
                                );
                            }}
                            startDecorator={
                                <>
                                    <Select
                                        value={countryCode}
                                        disabled={disabled}
                                        size={size}
                                        variant="plain"
                                        indicator={<ChevronDown size={16} />}
                                        sx={{
                                            ml: -1.5,
                                            mr: -0.5,
                                            width: 104,
                                            minWidth: 104,
                                            flexShrink: 0,
                                            bgcolor: "transparent",
                                            "--Select-focusedHighlight":
                                                "transparent",
                                            "--Select-focusedThickness": "0px",
                                            "&:hover": {
                                                bgcolor: "transparent",
                                            },
                                        }}
                                        slotProps={{
                                            button: {
                                                sx: {
                                                    px: 1,
                                                },
                                            },
                                            listbox: {
                                                placement: "bottom-start",
                                                sx: {
                                                    zIndex: 10001,
                                                    borderRadius: "4px",
                                                    minWidth: 200,
                                                    maxWidth: 260,
                                                    "--ListItem-paddingLeft":
                                                        "10px",
                                                    "--ListItem-paddingRight":
                                                        "10px",
                                                },
                                            },
                                        }}
                                        renderValue={(selected) => {
                                            const selectedCountry =
                                                getPhoneCountry(
                                                    selected?.value as string,
                                                );
                                            return (
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.75,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <FlagIcon
                                                        src={
                                                            selectedCountry.flagIcon
                                                        }
                                                        alt={
                                                            selectedCountry.name
                                                        }
                                                    />
                                                    <Typography level="body-sm">
                                                        {
                                                            selectedCountry.dialCode
                                                        }
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                        onChange={(_event, value) => {
                                            if (!value) {
                                                return;
                                            }
                                            const nextCode =
                                                value as PhoneCountryCode;
                                            setValue(countryName, nextCode, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            const current =
                                                getValues(name) || "";
                                            setValue(
                                                name,
                                                formatPhoneNumber(
                                                    current,
                                                    nextCode,
                                                ),
                                                {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                },
                                            );
                                        }}
                                    >
                                        {PHONE_COUNTRIES.map((option) => (
                                            <Option
                                                key={option.code}
                                                value={option.code}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "flex-start",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <FlagIcon
                                                        src={option.flagIcon}
                                                        alt={option.name}
                                                    />
                                                    <Typography level="body-sm">
                                                        {option.name}
                                                    </Typography>
                                                    <Typography
                                                        level="body-xs"
                                                        sx={{
                                                            color: "text.tertiary",
                                                        }}
                                                    >
                                                        {option.dialCode}
                                                    </Typography>
                                                </Box>
                                            </Option>
                                        ))}
                                    </Select>
                                    <Divider orientation="vertical" />
                                </>
                            }
                            sx={{
                                borderRadius: "4px",
                                ...sx,
                            }}
                        />
                    </Box>
                );
            }}
        />
    );
};

export default PhoneField;
